import {
  Canvas,
  FabricText,
  FabricObject,
  type TDataUrlOptions,
  InteractiveFabricObject,
} from "fabric";

import {
  RectPlus as Rect,
  ImagePlus as FabricImage,
  TrianglePlus as Triangle,
  TextBoxPlus as Textbox,
  CirclePlus as Circle,
} from "./shapes";
import { filters } from "./filters";
import { CanvasImage } from "./types";
import { loadFonts, loadImage } from "./utils";

export type EditorProps = {
  image: CanvasImage;
  loadImage: (src: string, params: any) => Promise<FabricImage>;
};

FabricObject.customProperties = ["name", "id"];

export class Renderer {
  _canvas: Canvas | null = null;
  _canvasElement: HTMLCanvasElement | null = null;

  image: EditorProps["image"];
  loadImage: EditorProps["loadImage"];

  sourceMap: Record<string, string> = {};

  constructor({ image, loadImage }: EditorProps) {
    InteractiveFabricObject.ownDefaults = {
      ...InteractiveFabricObject.ownDefaults,
      cornerColor: "#FFF",
      cornerStyle: "circle",
      borderColor: "#3b82f6",
      borderScaleFactor: 1.5,
      transparentCorners: false,
      borderOpacityWhenMoving: 1,
      cornerStrokeColor: "#3b82f6",
    };

    this.image = image;

    const { width, height } = image;

    this._canvas = new Canvas(undefined, {
      width: width,
      height: height,
    });

    this.loadImage = loadImage;
    this.loadImage.bind(this);
  }

  async loadFonts() {
    await loadFonts();
  }

  async createObject(object: FabricObject) {
    if (!this._canvas) {
      throw new Error("Canvas not set");
    }
    const { type, ...properties } = object;
    let obj: FabricObject | null | undefined = null;
    switch (type.toLowerCase()) {
      case "rect":
        obj = new Rect(properties);
        break;
      case "text":
        obj = new FabricText(properties.text, properties);
        break;
      case "textbox":
        obj = new Textbox(properties.text, properties);
        break;
      case "circle":
        obj = new Circle(properties);
        break;
      case "triangle":
        obj = new Triangle(properties);
        break;
      case "image":
        obj = await this.loadImage(properties.src, properties);
        if (properties.maxWidth || properties.maxHeight) {
          const maxWidth = properties.maxWidth || Infinity;
          const maxHeight = properties.maxHeight || Infinity;
          const scaleX = maxWidth / obj.width;
          const scaleY = maxHeight / obj.height;
          const scale = Math.min(scaleX, scaleY, 1);
          obj.scale(scale);
        }
        break;
      case "logo":
        obj = await this.loadImage(properties.src, properties);
        break;
      default:
        throw new Error("Invalid object type:" + type);
    }

    return obj;
  }

  addObject(object: FabricObject) {
    if (!this._canvas) return;
    this._canvas.add(object);
  }

  applyFilters(image: FabricImage, filtermap: any) {
    if (!this._canvas) return;

    image.filters = [];
    Object.entries(filtermap).map(([key, value]) => {
      if (value) {
        image.filters.push(new filters[key](value));
      }
    });
    image.applyFilters();
    this._canvas.renderAll();
  }

  removeOverlay() {
    if (!this._canvas) return;
    this._canvas.overlayImage = undefined;
    this._canvas.renderAll();
  }

  async addOverlayImage(src: string) {
    if (!this._canvas) return;
    this.image.overlay = src;
    if (!src) {
      this._canvas.overlayImage = undefined;
    } else {
      const image = await this.loadImage(src, {});
      image.scaleToWidth(this._canvas.width);
      image.scaleToHeight(this._canvas.height);

      this._canvas.overlayImage = image;
    }
    this._canvas.renderAll();
  }

  async addbackgroundImage(src: string) {
    if (!this._canvas) return;

    const image = await this.loadImage(src, {});

    if (this.image.width && this.image.height) {
      if (image.width > image.height) {
        image.scaleToHeight(this.image.height);
      } else {
        image.scaleToWidth(this.image.width);
      }

      this._canvas.setDimensions({
        width: this.image.width,
        height: this.image.height,
      });
    } else {
      this.image.width = image.width;
      this.image.height = image.height;

      this._canvas.setDimensions({
        width: image.width,
        height: image.height,
      });
    }

    this._canvas.calcOffset();

    if (this.image.filters) {
      this.applyFilters(image, this.image.filters);
    }
    image.canvas = this._canvas;
    this._canvas.backgroundImage = image;

    this._canvas.renderAll();
  }

  async initialiseCanvas() {
    if (!this._canvas) {
      throw new Error("Canvas not set");
    }
    this._canvas.clear();
    if (this.image.src) {
      await this.addbackgroundImage(this.image.src);
    }

    if (this.image.overlay) {
      await this.addOverlayImage(this.image.overlay);
    }

    for (let i = 0; i < this.image.objects?.length; i += 1) {
      const object = await this.createObject(this.image.objects[i]);
      this.addObject(object);
    }
  }

  async renderAll() {
    if (!this._canvas) {
      throw new Error("Canvas not set");
    }
    return this._canvas.renderAll();
  }

  toJSON() {
    if (!this._canvas) {
      throw new Error("Canvas not set");
    }
    return this._canvas.toJSON();
  }

  toDataUrl(options: TDataUrlOptions) {
    if (!this._canvas) {
      throw new Error("Canvas not set");
    }
    return this._canvas.toDataURL(options);
  }

  export() {
    if (!this._canvas) {
      throw new Error("Canvas not set");
    }

    const json = this.toJSON();

    const filters = json.backgroundImage.filters.reduce(
      (acc: Record<keyof Filters, any>, filter: Filters[keyof Filters]) => {
        acc[filter.type as keyof Filters] = filter;
        return acc;
      },
      {} as Record<keyof Filters, any>
    );

    const objects = json.objects.map((o: FabricObject) => {
      if ("src" in o) {
        o.src = this.sourceMap[o.src] || o.src;
      }
      return o;
    });

    return {
      filters,
      src: this.image.src,
      overlay: this.image.overlay,
      width: this._canvas.width,
      height: this._canvas.height,
      objects,
    };
  }

  exportImage() {
    const rendered = this.toDataUrl({
      format: "jpeg",
      quality: 0.9,
      multiplier: 2,
      enableRetinaScaling: true,
    });
    return {
      rendered: rendered,
      edits: this.export(),
    };
  }
}

export async function renderImage(
  data: CanvasImage,
  outputOptions: TDataUrlOptions = {
    format: "png",
    multiplier: 1,
  },
  loadImageFn: (src: string, params: any) => Promise<FabricImage> = loadImage
): Promise<string> {
  const renderer = new Renderer({
    image: data,
    loadImage: loadImageFn,
  });

  await renderer.initialiseCanvas();
  await renderer.loadFonts();

  const image = renderer._canvas?.toDataURL(outputOptions);

  if (!image) {
    throw new Error("Failed to render image");
  }

  return image;
}
